import { useState } from "react";
import { Loader2, FileSignature, Printer, GripVertical, Building2, Check, RefreshCw, Mail, CalendarClock, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { useAutosaveOnBlur, AutosaveBadge } from "./useAutosaveOnBlur";
import { DispoBackButton } from "./DispoBackButton";
import { previewPdfRequest } from "../../../lib/pdfPreview";

/**
 * Zulassung — Unterlagen-Checkliste & Übergabebeleg (Jun-2026 User-Wunsch).
 *
 * Zeigt die für die Zulassung benötigten Unterlagen als Checkliste mit
 * Eingangs- (vom Kunden) und Übergabe-Datum (an den Zulassungsdienst). Der
 * Zulassungsdienst wird aus einer Liste registrierter Dienstleister gewählt;
 * Standard zuerst: „Zulassung durch Händler selbst". Druck als Übergabebeleg.
 */
export function RegistrationModal({ listingId, data, onBack, onClose }) {
    const [saving, setSaving] = useState(false);
    const [docs, setDocs] = useState((data?.documents) || []);
    const [handoverDate, setHandoverDate] = useState(data?.handover_date || "");
    // Jun-2026 (User-Wunsch) — Kennzeichen + Rückgabetermin (Abholung) erfassen.
    const [licensePlate, setLicensePlate] = useState(data?.license_plate || "");
    const [returnDate, setReturnDate] = useState(data?.return_date || "");
    const [notifying, setNotifying] = useState(false);
    // Jun-2026 (User-Wunsch) — editierbarer E-Mail-Begleittext (KI-Erstentwurf);
    // Pflicht-Details/Links werden serverseitig (nicht editierbar) angehängt.
    const [showMail, setShowMail] = useState(false);
    const [mailText, setMailText] = useState("");
    const [mailDrafting, setMailDrafting] = useState(false);
    const suggestions = data?.provider_suggestions || [];

    const updDoc = (idx, k, v) => setDocs((prev) => prev.map((r, i) => i === idx ? { ...r, [k]: v } : r));
    const todayISO = () => new Date().toISOString().slice(0, 10);
    // Jun-2026 (User-Wunsch) — Anhaken setzt das Eingangsdatum automatisch auf heute
    // (überschreibt ein bereits gesetztes Datum nicht); Abhaken leert es wieder.
    const toggleDoc = (idx, checked) => setDocs((prev) => prev.map((r, i) =>
        i === idx ? { ...r, applicable: checked, received_date: checked ? (r.received_date || todayISO()) : "" } : r));

    // Auswahlliste: „Händler selbst" zuerst, danach registrierte Dienstleister.
    const buildOptions = () => [
        { key: "self", mode: "self", name: "Zulassung durch Händler selbst", contact: "", _self: true },
        ...suggestions.map((s) => ({
            key: s.id, mode: "external", provider_id: s.id, name: s.name,
            contact: [s.city, s.phone, s.contact_email].filter(Boolean).join(" · "),
        })),
    ];
    // Jun-2026 (User-Wunsch) — Zulassungsdienst per Drag&Drop sortieren; das
    // OBERSTE Element der Liste ist der ausgewählte Dienst. „Händler selbst" ist
    // Standard ganz oben. Beim Öffnen wird der gespeicherte Dienst nach oben gezogen.
    const [order, setOrder] = useState(() => {
        const opts = buildOptions();
        const savedKey = data?.service_provider?.mode === "external"
            ? (data.service_provider.provider_id || "")
            : "self";
        const idx = opts.findIndex((o) => o.key === savedKey);
        if (idx > 0) { const [picked] = opts.splice(idx, 1); opts.unshift(picked); }
        return opts;
    });
    const [dragIdx, setDragIdx] = useState(null);
    const selected = order[0] || { mode: "self", name: "Zulassung durch Händler selbst" };
    const isSelected = (o) => o.key === selected.key;

    const reorderTo = (from, to) => {
        if (from === to || from == null || to == null) return;
        setOrder((prev) => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };
    // Klick = Dienst auswählen → an die Spitze der Liste setzen.
    const selectOption = (idx) => reorderTo(idx, 0);

    const save = async ({ afterPrint = false, silent = false } = {}) => {
        if (!silent) setSaving(true);
        try {
            await api.put(`/listings/${listingId}/dispo/registration`, {
                documents: docs.map((d) => ({
                    key: d.key, applicable: d.applicable,
                    received_date: d.received_date || null,
                    value: d.value || "",
                })),
                handover_date: handoverDate || null,
                license_plate: licensePlate || "",
                return_date: returnDate || null,
                service_provider: selected.mode === "external"
                    ? { mode: "external", provider_id: selected.provider_id, name: selected.name, contact: selected.contact }
                    : { mode: "self" },
            });
            if (!silent) { toast.success("Zulassungsdaten gespeichert"); autosave.markSaved(); }
            if (afterPrint) {
                previewPdfRequest(
                    api.get(`/listings/${listingId}/dispo/registration.pdf`, { responseType: "blob" }),
                    "Zulassung.pdf",
                );
            }
            return true;
        } catch (e) {
            if (!silent) toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
            return false;
        } finally {
            if (!silent) setSaving(false);
        }
    };
    const autosave = useAutosaveOnBlur(
        () => save({ silent: true }),
        () => JSON.stringify({ docs, handoverDate, licensePlate, returnDate, provider: selected.key }),
    );

    // Jun-2026 (User-Wunsch) — Kunde informieren, dass die Zulassung erfolgt ist
    // und ein (Übergabe-)Termin vereinbart werden kann. Speichert vorher.
    const notifyCustomer = async () => {
        setNotifying(true);
        try {
            await save({ silent: true });
            const r = await api.post(`/listings/${listingId}/dispo/registration/notify-customer`, {
                license_plate: licensePlate || "",
                return_date: returnDate || null,
                custom_message: mailText || "",
            });
            toast.success(`Kunde benachrichtigt (${r.data.sent_to})${r.data.mocked ? " — Mock/Log (kein Live-Mailversand)" : ""}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Benachrichtigung fehlgeschlagen");
        } finally { setNotifying(false); }
    };

    // Jun-2026 (User-Wunsch) — KI-Erstentwurf des E-Mail-Begleittextes laden.
    const generateMailDraft = async () => {
        setMailDrafting(true);
        try {
            await save({ silent: true });
            const r = await api.post(`/listings/${listingId}/dispo/registration/email-draft`);
            setMailText(r.data?.message || "");
            toast.success(r.data?.ai ? "KI-Entwurf erstellt" : "Entwurf erstellt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entwurf fehlgeschlagen");
        } finally { setMailDrafting(false); }
    };
    const toggleMail = async () => {
        const next = !showMail;
        setShowMail(next);
        if (next && !mailText.trim()) await generateMailDraft();
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="registration-modal" onBlur={autosave.onBlur}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><FileSignature className="w-5 h-5 text-[#111827]" /> Zulassung — Unterlagen &amp; Übergabebeleg</DialogTitle>
                    <DialogDescription>Checkliste der für die Zulassung benötigten Unterlagen mit Eingangs- und Übergabe-Datum sowie Auswahl des Zulassungsdienstes.</DialogDescription>
                </DialogHeader>
                <AutosaveBadge status={autosave.status} savedAt={autosave.savedAt} />

                {(
                    <div className="space-y-5">
                        {/* Zurückgemeldeter Zulassungsstatus (vom Zulassungsdienst) */}
                        {data?.status?.registered && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 flex items-start gap-2" data-testid="registration-status-banner">
                                <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <span className="font-bold text-emerald-800">Zugelassen</span>
                                    <span className="text-emerald-700"> · am {data.status.registered_date} · Kennzeichen <strong>{data.status.license_plate}</strong></span>
                                    {data.status.note && <div className="text-xs text-emerald-700 mt-0.5">{data.status.note}</div>}
                                    <div className="text-[11px] text-emerald-600 mt-0.5">Kennzeichen wurde automatisch am Inserat hinterlegt.{data.status.handover_invited ? " Übergabetermin-Einladung an den Kunden gesendet." : ""}</div>
                                </div>
                            </div>
                        )}
                        {/* Zulassungsdienst-Auswahl (Drag&Drop — oberster = ausgewählt) */}
                        <div data-testid="registration-provider">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Zulassungsdienst</Label>
                            <p className="text-[11px] text-gray-600 mb-1.5">Per Drag&amp;Drop (Griff <GripVertical className="w-3 h-3 inline -mt-0.5" />) sortieren — der <strong>oberste</strong> Eintrag ist der ausgewählte Dienst. Standard ganz oben: „Zulassung durch Händler selbst".</p>
                            <div className="space-y-1.5">
                                {order.map((o, i) => (
                                    <div key={o.key}
                                        draggable
                                        onDragStart={() => setDragIdx(i)}
                                        onDragOver={(e) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== i) { reorderTo(dragIdx, i); setDragIdx(i); } }}
                                        onDragEnd={() => setDragIdx(null)}
                                        onClick={() => selectOption(i)}
                                        className={`w-full text-left rounded-sm border px-3 py-2 flex items-center gap-2 transition-colors cursor-pointer ${isSelected(o) ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"} ${dragIdx === i ? "opacity-60" : ""}`}
                                        data-testid={`registration-provider-option-${i}`}>
                                        <GripVertical className={`w-4 h-4 shrink-0 cursor-grab active:cursor-grabbing ${isSelected(o) ? "text-gray-300" : "text-gray-400"}`} data-testid={`registration-provider-grip-${i}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-bold truncate flex items-center gap-1.5">{o.name}{o._self && <span className={`text-[9px] font-bold uppercase tracking-wider rounded-sm px-1 py-0.5 ${isSelected(o) ? "bg-white/20" : "bg-gray-100 text-gray-500"}`}>Standard</span>}{isSelected(o) && <span className={`text-[9px] font-bold uppercase tracking-wider rounded-sm px-1 py-0.5 ${isSelected(o) ? "bg-emerald-500/30 text-white" : ""}`}>Ausgewählt</span>}</div>
                                            {o.contact && <div className={`text-[11px] truncate ${isSelected(o) ? "text-gray-300" : "text-gray-500"}`}>{o.contact}</div>}
                                        </div>
                                        {isSelected(o) && <Check className="w-4 h-4 shrink-0" />}
                                    </div>
                                ))}
                            </div>
                            {suggestions.length === 0 && (
                                <p className="text-[11px] text-amber-700 mt-1.5">Hinweis: Noch keine Zulassungsdienste registriert. Lege sie unter „Dienstleister" (Branche Zulassung) an, dann erscheinen sie hier zur Auswahl.</p>
                            )}
                        </div>

                        {/* Globales Übergabedatum (Abholung aller Unterlagen) */}
                        <div data-testid="registration-handover">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Übergabedatum (Abholung aller Unterlagen)</Label>
                            <p className="text-[11px] text-gray-600 mb-1.5">Datum, an dem alle Unterlagen an den Zulassungsdienst übergeben / abgeholt wurden.</p>
                            <Input type="date" value={handoverDate || ""} onChange={(e) => setHandoverDate(e.target.value)} className="h-9 w-48 text-sm" data-testid="registration-handover-date" />
                        </div>

                        {/* Jun-2026 (User-Wunsch) — Kennzeichen + Rückgabetermin (Abholung) + Kunde benachrichtigen */}
                        <div className="rounded-sm border border-sky-200 bg-sky-50/60 p-3" data-testid="registration-handback">
                            <Label className="text-xs font-bold uppercase tracking-wider text-sky-800 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Zulassung abschließen — Kennzeichen &amp; Rückgabetermin</Label>
                            <p className="text-[11px] text-sky-700/70 mb-2">Nach erfolgter Zulassung: Kennzeichen und (optional) Rückgabe-/Abholtermin erfassen und den Kunden benachrichtigen, dass ein Termin vereinbart werden kann.</p>
                            <div className="flex flex-wrap items-end gap-3">
                                <div>
                                    <Label className="text-[11px] text-gray-500">Kennzeichen</Label>
                                    <Input value={licensePlate} onChange={(e) => setLicensePlate(e.target.value.toUpperCase())} placeholder="z. B. M-AB 1234" className="h-9 w-44 text-sm font-mono-num uppercase" data-testid="registration-license-plate" />
                                </div>
                                <div>
                                    <Label className="text-[11px] text-gray-500">Rückgabe-/Abholtermin</Label>
                                    <Input type="date" value={returnDate || ""} onChange={(e) => setReturnDate(e.target.value)} className="h-9 w-48 text-sm" data-testid="registration-return-date" />
                                </div>
                                <Button type="button" onClick={notifyCustomer} disabled={notifying} className="h-9 rounded-sm bg-sky-600 hover:bg-sky-700 text-white flex items-center gap-1.5" data-testid="registration-notify-customer">
                                    {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Kunde benachrichtigen
                                </Button>
                            </div>
                            {/* Jun-2026 (User-Wunsch) — E-Mail-Begleittext (KI-Erstentwurf); Pflicht-Details/Links nicht editierbar */}
                            <div className="mt-2">
                                <button type="button" onClick={toggleMail} className="text-[11px] font-bold text-sky-700 hover:underline inline-flex items-center gap-1" data-testid="registration-mail-text-toggle">
                                    <Mail className="w-3.5 h-3.5" /> E-Mail-Text ansehen / anpassen
                                </button>
                                {showMail && (
                                    <div className="mt-1.5 space-y-1.5">
                                        <div className="flex items-center gap-2">
                                            <Button type="button" size="sm" variant="outline" onClick={generateMailDraft} disabled={mailDrafting} className="h-8 text-xs border-2" data-testid="registration-mail-ai-draft">
                                                {mailDrafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> KI-Entwurf</>}
                                            </Button>
                                            <span className="text-[10px] text-gray-500">Erstentwurf durch KI — frei anpassbar.</span>
                                        </div>
                                        <Textarea value={mailText} onChange={(e) => setMailText(e.target.value)} rows={6} className="text-xs" placeholder="E-Mail-Begleittext…" data-testid="registration-mail-text" />
                                        <div className="text-[10px] text-gray-600 bg-white border border-gray-200 rounded-sm px-2 py-1.5 flex items-start gap-1.5" data-testid="registration-mail-fixed-note">
                                            <Info className="w-3 h-3 mt-0.5 shrink-0 text-sky-500" />
                                            <span>Die <b>Fahrzeugdetails</b> (Fahrzeug, Kennzeichen, Zulassungsdatum, Abholtermin) sowie der Hinweis zur Terminvereinbarung werden automatisch &amp; <b>unveränderbar</b> an die E-Mail angehängt.</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Jun-2026 (User-Wunsch) — verfügbare Fahrzeugdokumente aus der
                            Disposition (angehakt) → werden dem Zulasser mit übergeben. */}
                        {(data?.available_vehicle_docs?.length > 0) && (
                            <div className="rounded-sm border border-emerald-200 bg-emerald-50/60 p-3" data-testid="registration-available-vehicle-docs">
                                <Label className="text-xs font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5"><FileSignature className="w-3.5 h-3.5" /> Verfügbare Fahrzeugdokumente (aus Disposition)</Label>
                                <p className="text-[11px] text-emerald-700/80 mb-2">Diese Dokumente sind laut Disposition vorhanden und werden dem Zulasser mit übergeben.</p>
                                <ul className="space-y-1">
                                    {data.available_vehicle_docs.map((v) => (
                                        <li key={v.key} className="text-sm text-emerald-900 flex items-center gap-2" data-testid={`registration-avail-doc-${v.key}`}>
                                            <Check className="w-4 h-4 text-emerald-600 shrink-0" /> {v.label}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Unterlagen-Checkliste */}
                        <div data-testid="registration-documents">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-800">Benötigte Unterlagen</Label>
                            <p className="text-[11px] text-gray-700 mb-2">Anhaken = vom Kunden eingegangen → Eingangsdatum wird automatisch auf heute gesetzt (anpassbar). Das Übergabedatum an den Zulassungsdienst gilt gesammelt (siehe oben).</p>
                            <div className="space-y-1.5">
                                {docs.map((d, idx) => (
                                    <div key={d.key} className={`rounded-sm border px-2.5 py-2 ${d.applicable ? "border-gray-200" : "border-gray-100 bg-gray-50"}`} data-testid={`registration-doc-${d.key}`}>
                                        <div className="flex items-center gap-2">
                                            <input type="checkbox" checked={d.applicable !== false} onChange={(e) => toggleDoc(idx, e.target.checked)} className="accent-[#111827] shrink-0" data-testid={`registration-doc-applicable-${d.key}`} />
                                            <div className={`flex-1 min-w-0 text-sm font-medium ${d.applicable ? "" : "text-gray-400"}`}>{d.label}</div>
                                            <Input type="date" value={d.received_date || ""} disabled={d.applicable === false} onChange={(e) => updDoc(idx, "received_date", e.target.value)} className="h-8 w-36 text-xs shrink-0" data-testid={`registration-doc-received-${d.key}`} />
                                        </div>
                                        {d.has_value && d.applicable !== false && (
                                            <Input value={d.value || ""} onChange={(e) => updDoc(idx, "value", e.target.value)} placeholder={d.key === "evb_nummer" ? "eVB-Nummer…" : "Wunschkennzeichen…"} className="h-8 mt-1.5 ml-6 text-xs max-w-xs" data-testid={`registration-doc-value-${d.key}`} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {!data?.status?.registered && data?.status_url && (
                    <p className="text-[11px] text-gray-600 border-t pt-2" data-testid="registration-status-link-note">
                        Der Übergabebeleg enthält einen QR-Code/Link, über den der Zulassungsdienst „zugelassen am / Kennzeichen" direkt zurückmeldet (Kennzeichen wird dann automatisch am Inserat hinterlegt und der Kunde per E-Mail informiert).
                    </p>
                )}

                <DialogFooter className="gap-2">
                    <DispoBackButton onBack={onBack} />
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" data-testid="registration-close">Schließen</Button>
                    <Button type="button" onClick={() => save()} disabled={saving} className="rounded-sm bg-[#111827] hover:bg-black text-white flex items-center gap-1.5" data-testid="registration-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Aktualisieren</>}
                    </Button>
                    <Button type="button" onClick={() => save({ afterPrint: true })} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5" data-testid="registration-print">
                        <Printer className="w-4 h-4" /> Übergabebeleg drucken
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
